import {
  Badge,
  Dropdown,
  Icon,
  Portal,
  Text,
  Timestamp,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import {
  ConversationDirection,
  ConversationInfoType,
  ConversationsChannels,
  ConversationStatus,
  ConversationSummaryLabel,
} from 'lib-shared-legacy-types';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { getHumanReadableExpiration } from 'components/conversation/utils/conversationHumanReadableExpiration';
import { Highlight } from 'components/highlight';
import { buttonize } from 'components/shared/util/buttonize';

import { ConversationStatusIcon } from '../conversationStatusIcon';

import styles from './styles.module.scss';

type TimeoutID = NodeJS.Timeout;

type ConversationSummaryProps = {
  /** Extra classname(s) */
  className?: string;
  /** Show small version */
  small?: boolean;
  /** Show light version (for dark backgrounds) */
  light?: boolean;
  /** Dropdown to show when clicking the status text */
  dropdown?: React.ReactNode;
  /** Loading */
  loading?: boolean;
  /** Show updating state : */
  updating?: boolean;
  /** Conversation URL */
  conversationUrl?: string;
  /** Conversation info */
  conversationInfo: ConversationInfoType;
  /** Conversation attributes. */
  conversationAttributes?: Record<string, string>;
  /** Timestamp of when an Agent can't write in the conversation any longer */
  replyWindowExpiresOn?: Date;
  /** Labels */
  labels: ConversationSummaryLabel;
  /** When the title link is clicked */
  onClickTitleLink?: () => void;
  /** When the status link is clicked */
  onClickStatusLink?: () => void;
};

type StateT = {
  now: number;
  dropdownOpen: boolean;
  dropdownOffset: { left: number; top: number };
};

export class ConversationSummary extends React.PureComponent<
  ConversationSummaryProps,
  StateT
> {
  timeout?: TimeoutID;
  updateReplyWindowTimeout?: TimeoutID;

  state = {
    now: Date.now(),
    dropdownOpen: false,
    dropdownOffset: { left: 0, top: 0 },
  };

  getTitle(
    title: string,
    conversationUrl: string | null = null,
    requester?: string | null,
  ) {
    const content = [
      requester && (
        <Text.Heading as="span" variant="heading-2" key="requester">
          {requester}
        </Text.Heading>
      ),
      requester && <span key="separator"> - </span>,
      <Text.Paragraph as="span" variant="2" key="title">
        <Highlight highlightedValue={title} />
      </Text.Paragraph>,
    ].filter(Boolean);

    if (!conversationUrl) return content;

    return (
      <Link
        to={conversationUrl}
        className={styles.titleLink}
        onClick={this.props.onClickTitleLink}
      >
        {content}
      </Link>
    );
  }

  componentDidMount() {
    this.setNextReplyWindowRefreshTimeout();
  }

  componentDidUpdate(prevProps) {
    const { replyWindowExpiresOn } = this.props;

    if (replyWindowExpiresOn !== prevProps.replyWindowExpiresOn) {
      if (this.updateReplyWindowTimeout) {
        clearTimeout(this.updateReplyWindowTimeout);
      }

      if (replyWindowExpiresOn) {
        this.setNextReplyWindowRefreshTimeout();
      }
    }
  }

  componentWillUnmount() {
    if (this.updateReplyWindowTimeout) {
      clearTimeout(this.updateReplyWindowTimeout);
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  @boundMethod
  setNextReplyWindowRefreshTimeout() {
    const { now } = this.state;
    const { replyWindowExpiresOn } = this.props;

    if (!replyWindowExpiresOn) {
      return;
    }

    const smallerTimeout = Math.min(replyWindowExpiresOn.getTime() - now, 5000);
    const timeout = Math.max(smallerTimeout, 0);
    if (timeout > 0) {
      this.updateReplyWindowTimeout = global.setTimeout(
        this.updateReplyWindow,
        timeout,
      );
    }
  }

  @boundMethod
  updateReplyWindow() {
    if (this.updateReplyWindowTimeout) {
      clearTimeout(this.updateReplyWindowTimeout);
    }
    this.setState({ now: Date.now() }, this.setNextReplyWindowRefreshTimeout);
  }

  @boundMethod
  handleDropdownToggle(event: React.MouseEvent<HTMLSpanElement>) {
    const { dropdownOpen } = this.state;
    if (!dropdownOpen) this.calculateOffset(event.currentTarget);

    this.timeout = global.setTimeout(() => {
      this.setState({
        dropdownOpen: !dropdownOpen,
      });
    }, 0);
  }

  calculateOffset(target: HTMLSpanElement) {
    const { left, top } = target.getBoundingClientRect();

    this.setState({
      dropdownOffset: {
        left,
        top,
      },
    });
  }

  @boundMethod
  closeDropdown() {
    this.setState({
      dropdownOpen: false,
    });
  }

  hasFollowUp(status?: string | null) {
    return status === 'pending' || status === 'awaitingpending';
  }

  getStatusText() {
    const { labels, conversationInfo } = this.props;

    return labels.queue || conversationInfo.queue?.name ? (
      <strong>{labels.queue || conversationInfo.queue?.name}</strong>
    ) : (
      <i>Not in a queue</i>
    );
  }

  render() {
    const {
      className,
      small,
      light,
      dropdown,
      loading,
      updating,
      conversationUrl,
      conversationInfo,
      conversationAttributes,
      replyWindowExpiresOn,
      onClickStatusLink,
      onClickTitleLink,
      labels,
      ...otherProps
    } = this.props;

    const { dropdownOpen, dropdownOffset } = this.state;

    const {
      id: UUID,
      numeric_id: numericCSID = false,
      status,
      initial_channel: channel,
      direction,
      pending_expiration_at: pendingAt,
    } = conversationInfo;

    const csid =
      typeof numericCSID === 'number' ? numericCSID.toString() : UUID;
    const csidString =
      typeof csid === 'string' || typeof csid === 'number' ? csid : '';

    const { title, requester } = labels;

    const hasFollowUp = status === 'pending' || status === 'awaitingpending';

    const rootClass = classnames(
      {
        [styles.summary]: true,
        [styles.summarySmall]: small,
        [styles.summaryLight]: light,
        [styles.summaryLoading]: !!loading,
        [styles.summaryUpdating]: updating,
      },
      className,
    );

    const titleString = title || `#${csidString}`;

    const statusText = this.getStatusText();

    const renderParameters = {
      otherProps,
      rootClass,
      channel,
      direction,
      status,
      loading,
      titleString,
      conversationUrl,
      conversationAttributes,
      requester,
      replyWindowExpiresOn,
      hasFollowUp,
      pendingAt,
      updating,
      dropdown,
      statusText,
      dropdownOffset,
      dropdownOpen,
    };

    return this.renderContent(renderParameters, csidString);
  }

  renderContent(params: RenderParameters, csidString: string | number) {
    const separator = ' · ';

    const {
      otherProps,
      rootClass,
      channel,
      direction,
      status,
      loading,
      titleString,
      conversationUrl,
      conversationAttributes,
      requester,
      replyWindowExpiresOn,
      hasFollowUp,
      pendingAt,
      updating,
      dropdown,
      statusText,
      dropdownOffset,
      dropdownOpen,
    } = params;

    return (
      <div {...otherProps} className={rootClass}>
        {direction && (
          <ConversationStatusIcon
            channel={channel}
            direction={direction}
            status={status}
            loading={loading}
            className={styles.iconHolder}
          />
        )}

        <div className={styles.textWrap}>
          {loading && <div className={styles.loadingTitle} />}
          {loading && <div className={styles.loadingStatus} />}

          <div className={styles.title}>
            <span className={styles.titleWrap}>
              {this.getTitle(titleString, conversationUrl, requester)}
            </span>

            <span className={styles.titlePills}>
              {channel === 'facebookmessenger' &&
                conversationAttributes?.instagram_page_id &&
                conversationAttributes?.instagram_profile_id && (
                  <Badge
                    variant="primary"
                    size="small"
                    className={styles.badge}
                  >
                    Instagram
                  </Badge>
                )}

              {replyWindowExpiresOn && (
                <Badge variant="warning" size="small" className={styles.badge}>
                  {getHumanReadableExpiration(
                    this.state.now,
                    this.props.replyWindowExpiresOn,
                  )}
                </Badge>
              )}

              {hasFollowUp && !!pendingAt && (
                <Badge variant="info" size="small" className={styles.badge}>
                  Expires <Timestamp relative time={pendingAt} />
                </Badge>
              )}

              {updating && (
                <Badge
                  variant="secondary"
                  size="small"
                  className={styles.badge}
                >
                  Updating...
                </Badge>
              )}
            </span>
          </div>
        </div>

        {!dropdown && <div className={styles.status}>{statusText}</div>}

        {dropdown && (
          <div className={styles.status}>
            {csidString && (
              <span
                className={styles.csidString}
              >{`#${csidString}${separator}`}</span>
            )}
            <span {...buttonize(this.handleDropdownToggle)}>
              {statusText}
              <Icon name="info" className={styles.infoIcon} />
            </span>
          </div>
        )}

        {dropdown && (
          <Portal containerId="conversation-summary-dropdown-container">
            <div
              className={styles.dropdownHolder}
              style={{
                left: `${dropdownOffset.left}px`,
                top: `${dropdownOffset.top}px`,
              }}
            >
              <Dropdown
                className={styles.dropdown}
                isOpen={dropdownOpen}
                onClose={this.closeDropdown}
              >
                {dropdown}
              </Dropdown>
            </div>
          </Portal>
        )}
      </div>
    );
  }
}

type RenderParameters = {
  otherProps: React.HtmlHTMLAttributes<HTMLDivElement>;
  rootClass: string;
  channel?: ConversationsChannels | null;
  direction?: ConversationDirection | null;
  status?: ConversationStatus | null;
  loading: boolean | undefined;
  titleString: string;
  conversationUrl: string | undefined;
  conversationAttributes?: Record<string, string>;
  requester: string | undefined | null;
  replyWindowExpiresOn: Date | undefined;
  hasFollowUp: boolean;
  pendingAt: string | undefined | null;
  updating: boolean | undefined;
  dropdown: React.ReactNode;
  statusText: React.ReactNode;
  dropdownOffset: { left: number; top: number };
  dropdownOpen: boolean;
};
